/* eslint-disable no-console */
/* eslint-disable no-undef */
/** Above lint rule is disabled to prevent errors with __webpack_init_sharing__ and __webpack_share_scopes__ */

/**
 * Created using the Module Federation documentation on Dynamic Remote Containers
 * This way we can lazy load each remote container and module
 * https://webpack.js.org/concepts/module-federation/#dynamic-remote-containers
 */

/**
 * @description Loads a script dynamically and returns a promise that resolves to the global scope variable
 * @param {string} url - The URL of the script to load
 * @param {string} scope - The global scope variable that the script will expose
 * @returns {Promise} A promise that resolves to the global scope variable on successful script load
 */
const isModuleLoaderDebugEnabled = () => {
    try {
        return (
            window?.localStorage?.getItem('debug:module-loader') === '1' ||
            window?.location?.search?.includes('debugModuleLoader=1')
        );
    } catch {
        return false;
    }
};

const debugModuleLoader = (...args) => {
    if (isModuleLoaderDebugEnabled()) {
        console.info('[module-loader]', ...args);
    }
};

const loadScope = async (url, scope) => {
    // Create a script element
    const element = document.createElement('script');
    // Create a promise to handle the script loading process
    const promise = new Promise((resolve, reject) => {
        // Set the script's source URL
        element.src = url;

        // Set the script type to JavaScript
        element.type = 'text/javascript';

        // Load the script asynchronously
        element.async = true;

        // Reject the promise when the script fails to load
        element.onerror = () => {
            reject(new Error(`Failed to load script from ${url}.`));
        };

        // Resolve the promise with the global scope variable when the script loads successfully
        element.onload = () => {
            resolve(window[scope]);
        };

        // Reject the promise if the script fails to load
        element.onerror = () => {
            reject(new Error(`Failed to load script from ${url}.`));
        };
    });
    // Append the script element to the document head to start loading
    document.head.appendChild(element);
    // Remove the script element once the promise settles
    promise.finally(() => document.head.removeChild(element));
    // Return the promise
    return promise;
};

/**
 * @description Loads a module from a remote container dynamically
 * @param {string} url - The URL of the remote container
 * @param {string} scope - The global scope variable that the remote container will expose
 * @param {string} module - The module to load from the remote container. Defaults to '.' to load the default module.
 * @returns {Promise} A promise that resolves to the module exported by the remote container
 */
const deriveMasterUrl = (url) => {
    const entryPrefix = window?.env?.ENTRY_PREFIX;

    if (!entryPrefix) {
        return '';
    }

    const normalizedPrefix = `/${entryPrefix.replace(/^\/+|\/+$/g, '')}`;

    try {
        const parsedUrl = new URL(url, window.location.origin);
        if (!parsedUrl.pathname.startsWith(`${normalizedPrefix}/`)) {
            return '';
        }

        const fallbackPathname = parsedUrl.pathname.slice(
            normalizedPrefix.length
        );
        return `${parsedUrl.origin}${fallbackPathname}`;
    } catch {
        return '';
    }
};

const loadModule = async (url, scope, backupUrl = '', module = '.') => {
    try {
        debugModuleLoader('load start', { url, scope, module, backupUrl });
        // Load the remote container and get its exposed global scope variable
        const container = await loadScope(url, scope);
        // Initialize sharing of the default scope. Necessary for Module Federation to share modules across different builds
        await __webpack_init_sharing__('default');
        // Initialize the container, providing it access to shared modules
        await container.init(__webpack_share_scopes__.default);
        // Get the module factory from the container
        const factory = await container.get(module);
        // Execute the factory to get the module
        debugModuleLoader('load success', { url, scope, module });
        return factory();
    } catch (primaryError) {
        const fallbackUrl =
            backupUrl && backupUrl !== url ? backupUrl : deriveMasterUrl(url);

        if (fallbackUrl && fallbackUrl !== url) {
            debugModuleLoader('primary load failed, trying fallback', {
                url,
                fallbackUrl,
                scope,
                module,
                primaryError,
            });

            try {
                // Retry with fallback URL if provided
                const container = await loadScope(fallbackUrl, scope);
                await __webpack_init_sharing__('default');
                await container.init(__webpack_share_scopes__.default);
                const factory = await container.get(module);
                debugModuleLoader('fallback load success', {
                    fallbackUrl,
                    scope,
                    module,
                });
                return factory();
            } catch (fallbackError) {
                debugModuleLoader('fallback load failed', {
                    url,
                    fallbackUrl,
                    scope,
                    module,
                    fallbackError,
                });

                throw new Error(
                    `Failed to load module '${scope}/${module}' from both '${url}' and fallback '${fallbackUrl}'.`
                );
            }
        } else {
            debugModuleLoader('load failed without fallback', {
                url,
                scope,
                module,
                primaryError,
            });

            throw new Error(
                `Failed to load module '${scope}/${module}' from '${url}'.`
            );
        }
    }
};

export { loadModule };
